import React, { useMemo } from 'react';
import { ErrorMessageTW } from '../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { Card, FlexRow, Grid, NavigationItem } from '../../../ui-toolkit';
import { Form, Formik } from 'formik';
import { eTagType } from '../../../types/tags/eTagType';
import { TextFieldTW } from '../../../tailwindComponents/textInputTW/TextFieldTW';
import { SelectFieldTW } from '../../../tailwindComponents/selectTW/SelectFieldTW';
import { ButtonTW } from '../../../tailwindComponents/buttonTW/ButtonTW';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { useHistory } from 'react-router-dom';
import { SelectOption } from '../../../tailwindComponents/selectTW/interfaces';
import { useEditTagMutation, useListTagsQuery } from '../../../API/tags/TagApi';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import { IEditTagRequest } from '../../../types/tags/IEditTagRequest';
import { ITagData } from '../../../types/tags/ITagData';
import { generateEditTagScheme } from '../../../validation/administration/EditTagValidator';
import { ToggleTW } from '../../../tailwindComponents/toggleTW/ToggleTW';
import { upperFirst } from 'lodash';

interface IEditTagPageProps {
  tagId: string;
  tags: ITagData[];
}

export const EditTagPage: React.FC<IEditTagPageProps> = ({ tagId, tags }) => {
  const history = useHistory();
  const currentTag = tags.find((tag) => tag.id === tagId);
  if (!currentTag) {
    window.location.href = ClientUrlFactory().urlFor.administrationListTags();
  }

  const typeOptions: SelectOption[] = useMemo(
    () => Object.values(eTagType).map((t) => ({ label: upperFirst(t.toString()), value: t.toString() })),
    []
  );

  const [editTag, { error, isLoading }] = useEditTagMutation();
  const { data: allTags = [], isLoading: isAutomationTagsLoading } = useListTagsQuery({
    isActive: true,
    lookupAutomations: true,
  });

  const isTagInAutomation = useMemo(() => {
    if (isAutomationTagsLoading || !currentTag || allTags.length === 0) return false;

    return allTags.some((tag) => (tag.isInAutomations ?? false) && tag.label === currentTag.label);
  }, [isAutomationTagsLoading, currentTag, allTags]);

  const onEdit = async (request: IEditTagRequest) => {
    if (isTagInAutomation) return; // Prevent submitting if restricted
    const result = await editTag({ id: tagId, body: request });
    if (checkForMutationSuccess(result, `Tag Edited`, 'Error Editing Tag')) {
      history.push(ClientUrlFactory().urlFor.administrationListTags());
    }
  };

  const editTagScheme = useMemo(() => {
    return generateEditTagScheme(allTags, currentTag?.label ?? '');
  }, [allTags, currentTag]);

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      <Grid width={'440px'}>
        <Card title={'Edit Tag'} data-test-id={'edit-tag-page'}>
          <Formik
            initialValues={{
              label: currentTag?.label || '',
              type: currentTag?.type as eTagType,
              isActive: currentTag?.isActive || false,
            }}
            onSubmit={onEdit}
            validationSchema={editTagScheme}
          >
            {({ isValid, dirty }) => (
              <Form>
                <TextFieldTW
                  name={'label'}
                  labelText={'Name'}
                  data-test-id={'tag-name'}
                  isRequired
                  disabled={isAutomationTagsLoading || isTagInAutomation}
                />
                <SelectFieldTW
                  name={'type'}
                  options={typeOptions}
                  labelText={'Type'}
                  data-test-id={'tag-type'}
                  isRequired
                  disabled={isAutomationTagsLoading || isTagInAutomation}
                />
                <ToggleTW
                  name="isActive"
                  text="Is Active?"
                  className={`mb-2 mt-6 ${isAutomationTagsLoading || isTagInAutomation ? 'pointer-events-none opacity-50' : ''}`}
                  data-test-id="tag-active"
                  disabled={isAutomationTagsLoading || isTagInAutomation}
                />
                {isAutomationTagsLoading && <div className="mb-4 text-sm text-gray-500">Loading tag data...</div>}
                {isTagInAutomation && (
                  <div className="mb-4">
                    <ErrorMessageTW message="This tag is currently used in back-end automations and cannot be edited." />
                  </div>
                )}
                <FlexRow justifyContent={'start'} width={100}>
                  <ButtonTW
                    type={'submit'}
                    disabled={isLoading || !isValid || !dirty || isTagInAutomation}
                    data-test-id={'tag-create'}
                  >
                    Save
                  </ButtonTW>
                  <NavigationItem to={ClientUrlFactory().urlFor.administrationListTags()} text={'Cancel'}>
                    <ButtonTW data-test-id={'tag-cancel'} secondary>
                      Cancel
                    </ButtonTW>
                  </NavigationItem>
                </FlexRow>
              </Form>
            )}
          </Formik>
        </Card>
      </Grid>
    </>
  );
};
